import React from 'react';
import { Box, Grid, Paper, Typography, Card, CardContent, List, ListItem, ListItemText, Chip, Divider } from '@mui/material';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { usageByDepartment, usageByService, dailyUsageTrend, recentAlerts, topUsers } from '../data/mockData';

// Register the chart components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Dashboard = () => {
  // Chart data for usage by department
  const departmentChartData = {
    labels: usageByDepartment.map(item => item.department),
    datasets: [
      {
        label: 'AI Usage by Department',
        data: usageByDepartment.map(item => item.count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Chart data for usage by service
  const serviceChartData = {
    labels: usageByService.map(item => item.service),
    datasets: [
      {
        label: 'AI Usage by Service',
        data: usageByService.map(item => item.count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(201, 203, 207, 0.6)',
          'rgba(255, 99, 255, 0.6)',
          'rgba(54, 162, 64, 0.6)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Chart data for daily usage trend
  const trendChartData = {
    labels: dailyUsageTrend.map(item => item.date),
    datasets: [
      {
        label: 'Daily AI Usage Trend',
        data: dailyUsageTrend.map(item => item.count),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };

  const renderSeverityChip = (severity) => {
    let color = 'default';
    
    switch (severity) {
      case 'high':
        color = 'error';
        break;
      case 'medium':
        color = 'warning';
        break;
      case 'low':
        color = 'info';
        break;
      default:
        color = 'default';
    }
    
    return (
      <Chip 
        label={severity.charAt(0).toUpperCase() + severity.slice(1)} 
        color={color} 
        size="small" 
        sx={{ ml: 1 }}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" gutterBottom>
          Welcome to ShadowAI Monitor
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px' }}>
          Your comprehensive solution for monitoring AI usage across your organization. 
          Track AI service adoption, understand data sharing patterns, and enforce compliance policies.
        </Typography>
      </Box>
      
      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={3}>
          <Card 
            sx={{ 
              height: '100%', 
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid rgba(159, 215, 208, 0.2)',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: theme => theme.palette.primary.main }}>
                Total AI Services Detected
              </Typography>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 500 }}>
                {usageByService.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card 
            sx={{ 
              height: '100%', 
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid rgba(159, 215, 208, 0.2)',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: theme => theme.palette.primary.main }}>
                Total AI Queries Today
              </Typography>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 500 }}>
                {dailyUsageTrend[dailyUsageTrend.length - 1].count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card 
            sx={{ 
              height: '100%', 
              bgcolor: 'background.paper',
              borderRadius: 2, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid rgba(159, 215, 208, 0.2)',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: theme => theme.palette.primary.main }}>
                Departments Using AI
              </Typography>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 500 }}>
                {usageByDepartment.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card 
            sx={{ 
              height: '100%', 
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid rgba(159, 215, 208, 0.2)',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: theme => theme.palette.primary.main }}>
                Active AI Users
              </Typography>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 500 }}>
                {topUsers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Charts */}
        <Grid item xs={12} md={6}>
          <Paper 
            sx={{ 
              p: 3, 
              height: '100%', 
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid rgba(159, 215, 208, 0.2)',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: theme => theme.palette.primary.main }}>
              AI Usage by Department
            </Typography>
            <Box sx={{ height: 300, pt: 2 }}>
              <Bar 
                data={{
                  ...departmentChartData,
                  datasets: [
                    {
                      ...departmentChartData.datasets[0],
                      backgroundColor: [
                        'rgba(159, 215, 208, 0.7)',
                        'rgba(129, 195, 188, 0.7)',
                        'rgba(109, 175, 168, 0.7)',
                        'rgba(89, 155, 148, 0.7)',
                        'rgba(69, 135, 128, 0.7)',
                      ],
                      borderColor: [
                        'rgba(159, 215, 208, 1)',
                        'rgba(129, 195, 188, 1)',
                        'rgba(109, 175, 168, 1)',
                        'rgba(89, 155, 148, 1)',
                        'rgba(69, 135, 128, 1)',
                      ],
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      labels: {
                        color: 'white'
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(255, 255, 255, 0.05)'
                      },
                      ticks: {
                        color: 'rgba(255, 255, 255, 0.7)'
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(255, 255, 255, 0.05)'
                      },
                      ticks: {
                        color: 'rgba(255, 255, 255, 0.7)'
                      }
                    }
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper 
            sx={{ 
              p: 3, 
              height: '100%', 
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid rgba(159, 215, 208, 0.2)',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: theme => theme.palette.primary.main }}>
              AI Usage by Service
            </Typography>
            <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', pt: 2 }}>
              <Pie 
                data={{
                  ...serviceChartData,
                  datasets: [
                    {
                      ...serviceChartData.datasets[0],
                      backgroundColor: [
                        'rgba(159, 215, 208, 0.8)',
                        'rgba(139, 205, 198, 0.8)',
                        'rgba(119, 195, 188, 0.8)',
                        'rgba(99, 185, 178, 0.8)',
                        'rgba(79, 175, 168, 0.8)',
                        'rgba(59, 165, 158, 0.8)',
                        'rgba(49, 155, 148, 0.8)',
                        'rgba(39, 145, 138, 0.8)',
                        'rgba(29, 135, 128, 0.8)',
                      ],
                      borderWidth: 1,
                      borderColor: '#3B4859',
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        color: 'white',
                        padding: 15
                      }
                    }
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Paper 
            sx={{ 
              p: 3, 
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid rgba(159, 215, 208, 0.2)',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: theme => theme.palette.primary.main }}>
              Daily AI Usage Trend
            </Typography>
            <Box sx={{ height: 300, pt: 2 }}>
              <Bar 
                data={{
                  ...trendChartData,
                  datasets: [
                    {
                      ...trendChartData.datasets[0],
                      backgroundColor: 'rgba(159, 215, 208, 0.7)',
                      borderColor: 'rgba(159, 215, 208, 1)',
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      labels: {
                        color: 'white'
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(255, 255, 255, 0.05)'
                      },
                      ticks: {
                        color: 'rgba(255, 255, 255, 0.7)'
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(255, 255, 255, 0.05)'
                      },
                      ticks: {
                        color: 'rgba(255, 255, 255, 0.7)'
                      }
                    }
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Recent Alerts */}
        <Grid item xs={12} md={6}>
          <Paper 
            sx={{ 
              p: 3, 
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid rgba(159, 215, 208, 0.2)',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: theme => theme.palette.primary.main }}>
              Recent Alerts
            </Typography>
            <List>
              {recentAlerts.map((alert) => (
                <React.Fragment key={alert.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle1">{alert.type}</Typography>
                          {renderSeverityChip(alert.severity)}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(alert.timestamp).toLocaleString()}
                          </Typography>
                          <Typography variant="body2">
                            {alert.message}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  <Divider component="li" sx={{ borderColor: 'rgba(159, 215, 208, 0.1)' }} />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
        
        {/* Top Users */}
        <Grid item xs={12} md={6}>
          <Paper 
            sx={{ 
              p: 3, 
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', 
              border: '1px solid rgba(159, 215, 208, 0.2)',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: theme => theme.palette.primary.main }}>
              Top AI Users
            </Typography>
            <List>
              {topUsers.map((user, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1">
                          {user.username} ({user.department})
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">
                            Services: {user.services.join(', ')}
                          </Typography>
                          <Typography variant="body2">
                            Query Count: {user.queryCount}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  <Divider component="li" sx={{ borderColor: 'rgba(159, 215, 208, 0.1)' }} />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;